import { h, FunctionalComponent } from "preact";

import "./Root.scss";
import OnAir from "../OnAir/OnAir";
import Schedule from "../Schedule/Schedule";
import OnAirSmall from "../OnAirSmall/OnAirSmall";
import {Ref, useLayoutEffect, useRef, useState} from "preact/hooks";
import Tabs from "../Tabs/Tabs";
import MessageScreen from "../Message/Message";
import WebcamScreen from "../Webcam/Webcam";
import Catchup from "../Catchup/Catchup";
import { useQuery } from "@urql/preact";
import logo from "./URYplayer-logo.png";
import { useEffectEveryHourOnTheHour } from "../../lib/useEffectEveryHourOnTheHour";

const TermRoot: FunctionalComponent = ({}) => {
    const onAirWrapperRef = useRef<HTMLDivElement>();
    const scheduleRef = useRef<HTMLDivElement>();
    const messageRef = useRef<HTMLDivElement>();
    const webcamRef = useRef<HTMLDivElement>();
    const catchupRef = useRef<HTMLDivElement>();

    const onAirSmallWrapperRef = useRef<HTMLDivElement>();

    const heightsRef = useRef<number[]>();

    const [showSmall, setShowSmall] = useState(false);
    const [activeTabIndex, setActiveTabIndex] = useState(0);

    function scrollTo(target: "onair" | "schedule" | "message" | "webcam" | "catchup") {
        let ref: Ref<HTMLDivElement>;
        switch (target) {
            case "onair":
                ref = onAirWrapperRef;
                break;
            case "schedule":
                ref = scheduleRef;
                break;
            case "message":
                ref = messageRef;
                break;
            case "webcam":
                ref = webcamRef;
                break;
            case "catchup":
                ref = catchupRef;
                break;
            default: throw new Error("unknown " + target);
        }

        if (!ref.current) {
            return;
        }
        let scrollTop = ref.current.offsetTop;
        // Compensate for the small bar
        if (showSmall) {
            scrollTop -= onAirSmallWrapperRef.current?.clientHeight;
        }
        window.scrollTo({
            left: 0,
            top: scrollTop,
            behavior: "smooth"
        });
    }

    useLayoutEffect(() => {
        heightsRef.current = [
            0,
            onAirWrapperRef.current.clientHeight,
            scheduleRef.current.clientHeight + scheduleRef.current.offsetTop,
            messageRef.current.clientHeight + messageRef.current.offsetTop,
            webcamRef.current.clientHeight + webcamRef.current.offsetTop
        ]

        const onAirHeight = onAirWrapperRef.current.clientHeight;
        window.addEventListener("scroll", () => {
            setShowSmall(window.scrollY > onAirHeight);

            // Go backwards through the heights, find the first one that we're below
            for (let i = heightsRef.current.length - 1; i >= 0; i--) {
                const threshold = heightsRef.current[i] - (i > 0 ? onAirSmallWrapperRef.current.clientHeight : 0);
                if (window.scrollY > threshold) {
                    setActiveTabIndex(i);
                    break;
                }
            }
        })
    });

    return (
        <div class="wrapper">
            <OnAirSmall visible={showSmall} ref={onAirSmallWrapperRef} />
            <div class="root on-air">
                <main>
                    <OnAir ref={onAirWrapperRef} />
                    <Schedule ref={scheduleRef} />
                    <MessageScreen ref={messageRef} />
                    <WebcamScreen ref={webcamRef} />
                    <Catchup ref={catchupRef} />
                </main>
                <Tabs activeTabIndex={activeTabIndex} doScroll={scrollTo} />
            </div>
        </div>
    )
};

const OffAirRoot = () => {
    return (
        <div class="wrapper">
            <div class="root off-air">
                <div class="main">
                    <h1>Off Air</h1>
                    <p>We're not broadcasting right now, we'll be back next term!</p>
                </div>
                <div class="catchup">
                    <Catchup />
                </div>
            </div>
        </div>
    )
}

export const Root = () => {
    const [onAirResult, refetch] = useQuery({
        query: `
        query {
            isTerm
        }
        `
    });
    useEffectEveryHourOnTheHour(refetch);
    if (!onAirResult.data) {
        return (
            <div class="wrapper">
                <div class="root off-air">
                    <div class="main loading">
                        <img src={logo} alt="URY Player Logo" class="logo" />
                        <br />
                        <span class="btn">Loading...</span>
                    </div>
                </div>
            </div>
        )
    }
    if (onAirResult.data.isTerm) {
        return <TermRoot />
    } else {
        return <OffAirRoot />
    }
}
